import { msg } from "@lingui/core/macro";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { ClipboardList } from "@fleet/icons";
import {
  addPickingLineMutation,
  coercePickingKind,
  confirmPickingMutation,
  createPickingMutation,
  deletePickingLineMutation,
  deletePickingMutation,
  fromMinorQty,
  getLocationsQuery,
  getLotsByProductQuery,
  getLotsQuery,
  getMoveLinesByPickingQuery,
  getPickingsQuery,
  getProductsQuery,
  getQuantsByProductLocationQuery,
  isTracked,
  parseQtyInput,
  type PickingKind,
  queryKeys,
  restorePickingMutation,
  suggestFefoLot,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { useResetOnOrgSwitch } from "@/features/inventory/use-reset-on-org-switch";
import { BarcodeScanInput } from "@/features/barcode/barcode-scan-input";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { EmptyOrdersIllustration } from "@/components/ui/illustrations";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { useUndo } from "@/lib/undo";
import { createEffect, createMemo, createSignal, For, onMount, Show } from "solid-js";
import type {
  LocationRecord,
  LotRecord,
  ProductRecord,
  StockMoveLineRecord,
  StockPickingRecord,
  StockQuantRecord,
} from "@stockpilot/powersync";

function OperationsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();
  const search = useSearch({ from: "/_app/operations" });
  const navigate = useNavigate();

  const [kind, setKind] = createSignal<PickingKind>("receipt");
  const [srcLoc, setSrcLoc] = createSignal("");
  const [dstLoc, setDstLoc] = createSignal("");
  const [reference, setReference] = createSignal("");

  // The open operation is deep-linkable: the URL (`?picking=<id>`) is the source
  // of truth. Pasting/sharing `/operations?picking=<id>` opens exactly that
  // operation; reloading reconstructs it (idempotent). `setOpenPickingId` only
  // writes the URL.
  const openPickingId = (): string | null => search().picking ?? null;
  const setOpenPickingId = (id: string | null) => {
    void navigate({
      to: "/operations",
      search: id ? { picking: id } : {},
      replace: true,
    });
  };

  // Guard for the router's deferred flush: if boot lost the initial deep-link's
  // `?picking=`, reapply it synchronously from the snapshot.
  onMount(() => {
    const initialPicking = takeInitialSearchParam("picking", "/operations");
    if (initialPicking && !openPickingId()) {
      setOpenPickingId(initialPicking);
    }
  });

  // Multi-organization isolation: when the company changes, the open operation
  // belongs to the previous org → discard the pointer.
  useResetOnOrgSwitch(() => setOpenPickingId(null));

  // Line form state.
  const [lineProduct, setLineProduct] = createSignal("");
  const [lineQty, setLineQty] = createSignal("");
  const [lineLot, setLineLot] = createSignal("");

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const pickings = createPowerSyncQuery<StockPickingRecord>({
    queryKey: () => queryKeys.inventory.pickings(orgId()),
    queryFn: () => getPickingsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const openLines = createPowerSyncQuery<StockMoveLineRecord>({
    queryKey: () => queryKeys.inventory.pickingLines(openPickingId() ?? undefined),
    queryFn: () => getMoveLinesByPickingQuery(drizzle()!, openPickingId()!),
    db: () => db(),
    enabled: () => Boolean(drizzle() && openPickingId()),
  });

  const openPicking = createMemo(() => (pickings.data ?? []).find((p) => p.id === openPickingId()));

  // Resolves the deep-linked operation (`?picking=<id>`): present / loading /
  // not-found / offline. It prevents a silent empty panel when the operation does
  // not exist, was deleted, or the user lost access (PowerSync sync rules do not
  // download rows without permission → absent after sync = no access). It never
  // leaves a loader hanging: the timeout safety net guarantees that.
  const pickingResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(openPicking()),
    isPending: () => pickings.isLoading,
  });

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? id;
  });

  const selectedProduct = createMemo(() =>
    (products.data ?? []).find((p) => p.id === lineProduct()),
  );
  const lineProductTracked = createMemo(() => isTracked(selectedProduct()?.tracking));

  // Lots for the selected product (FEFO in the query: ascending expiry).
  const lineLots = createPowerSyncQuery<LotRecord>({
    queryKey: () => queryKeys.inventory.lotsByProduct(lineProduct() || undefined),
    queryFn: () => getLotsByProductQuery(drizzle()!, lineProduct()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && lineProduct()),
  });

  const allLots = createPowerSyncQuery<LotRecord>({
    queryKey: () => queryKeys.inventory.lots(orgId()),
    queryFn: () => getLotsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const lotName = createMemo(() => {
    const map = new Map((allLots.data ?? []).map((l) => [l.id, l.name]));
    return (id: string) => map.get(id) ?? id;
  });

  // On-hand by lot at the source location (for outbound FEFO).
  const srcLocationForLine = createMemo(() => {
    const p = openPicking();
    return p?.src_location_id ?? null;
  });

  const lineQuants = createPowerSyncQuery<StockQuantRecord>({
    queryKey: () =>
      queryKeys.inventory.quantsByLocation(
        lineProduct() || undefined,
        srcLocationForLine() ?? undefined,
      ),
    queryFn: () =>
      getQuantsByProductLocationQuery(drizzle()!, lineProduct(), srcLocationForLine()!),
    db: () => db(),
    enabled: () => Boolean(drizzle() && lineProduct() && srcLocationForLine()),
  });

  /**
   * Lot suggested by FEFO (earliest expiry among those with on-hand at the source
   * location). CC-6: the decision is pure (suggestFefoLot).
   */
  const fefoLot = createMemo(() => {
    const lots = lineLots.data ?? [];
    const quants = lineQuants.data ?? [];
    const onHandByLot = new Map<string, number>();
    for (const q of quants) {
      if (q.lot_id) {
        onHandByLot.set(q.lot_id, q.qty ?? 0);
      }
    }
    const availability = lots.map((l) => ({
      lot: { id: l.id, name: l.name, expiry_date: l.expiry_date },
      onHand: onHandByLot.get(l.id) ?? 0,
    }));
    return suggestFefoLot(availability);
  });

  // Auto-selects the FEFO lot for outbound movements of tracked products.
  createEffect(() => {
    const picking = openPicking();
    if (!picking || coercePickingKind(picking.kind) !== "delivery") {
      return;
    }
    if (!lineProductTracked()) {
      return;
    }
    const suggested = fefoLot();
    if (suggested && !lineLot()) {
      setLineLot(suggested.id);
    }
  });

  const kindLabel = (k: string | null) => {
    switch (k) {
      case "receipt": {
        return t(msg`Receipt`);
      }
      case "delivery": {
        return t(msg`Delivery`);
      }
      case "internal": {
        return t(msg`Internal transfer`);
      }
      default: {
        return k ?? "";
      }
    }
  };

  async function handleCreate(e: Event) {
    e.preventDefault();
    const d = drizzle();
    if (!d) {
      return;
    }
    const id = await createPickingMutation(d, orgId(), {
      kind: kind(),
      reference: reference().trim() || null,
      srcLocationId: srcLoc() || null,
      dstLocationId: dstLoc() || null,
    });
    setReference("");
    setOpenPickingId(id);
  }

  async function handleAddLine(productId: string, qtyRaw: string) {
    const d = drizzle();
    const picking = openPicking();
    const qty = parseQtyInput(qtyRaw);
    if (!d || !picking || !productId || qty === null || qty <= 0) {
      return;
    }
    // Tracked products require a lot on receipts/deliveries.
    const lotId = lineProductTracked() ? lineLot() || null : null;
    if (lineProductTracked() && !lotId) {
      return;
    }
    await addPickingLineMutation(
      d,
      orgId(),
      {
        id: picking.id,
        kind: coercePickingKind(picking.kind),
        src_location_id: picking.src_location_id,
        dst_location_id: picking.dst_location_id,
      },
      productId,
      qty,
      lotId,
    );
    setLineProduct("");
    setLineQty("");
    setLineLot("");
  }

  async function handleConfirm() {
    const d = drizzle();
    const picking = openPicking();
    if (!d || !picking) {
      return;
    }
    const lines = openLines.data ?? [];
    // CC-7: blocking confirmation (await). `confirmPickingMutation` can THROW
    // (verified phantom UPDATE, see
    // `@stockpilot/shared/inventory/mutations`): without this catch, a rejection
    // would surface as an unhandled rejection instead of notifying the user.
    try {
      await confirmPickingMutation(
        d,
        orgId(),
        {
          id: picking.id,
          kind: coercePickingKind(picking.kind),
          state: picking.state,
        },
        lines.map((l) => ({
          product_id: l.product_id,
          src_location_id: l.src_location_id,
          dst_location_id: l.dst_location_id,
          qty: l.qty,
          lot_id: l.lot_id,
        })),
      );
    } catch {
      showToast(t(msg`Could not confirm the operation`), "error");
    }
  }

  async function handleDeleteLine(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deletePickingLineMutation(d, id);
  }

  const { registerUndo } = useUndo();

  async function handleDeletePicking(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    try {
      // L2: soft-delete → trash (lines are preserved). L1: Undo toast.
      await deletePickingMutation(d, id);
      if (openPickingId() === id) {
        setOpenPickingId(null);
      }
      registerUndo(t(msg`Operation moved to trash`), async () => {
        await restorePickingMutation(d, id);
      });
    } catch {
      showToast(t(msg`Could not move the operation to trash`), "error");
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Operations`)}
        description={t(msg`Receipts, deliveries and internal transfers.`)}
        icon={ClipboardList}
      />

      <form
        class="mt-6 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreate}
        data-testid="picking-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Operation type`)}</label>
          <Select
            class="w-44"
            value={kind()}
            onChange={(v) => setKind(coercePickingKind(v))}
            data-testid="picking-kind-select"
            aria-label={t(msg`Operation type`)}
            options={[
              { value: "receipt", label: t(msg`Receipt`) },
              { value: "delivery", label: t(msg`Delivery`) },
              { value: "internal", label: t(msg`Internal transfer`) },
            ]}
          />
        </div>
        <Show when={kind() === "delivery" || kind() === "internal"}>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-muted-foreground">{t(msg`Source location`)}</label>
            <Select
              class="w-44"
              value={srcLoc()}
              onChange={(v) => setSrcLoc(v)}
              data-testid="picking-src-select"
              aria-label={t(msg`Source location`)}
              placeholder={t(msg`Select location`)}
              options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
            />
          </div>
        </Show>
        <Show when={kind() === "receipt" || kind() === "internal"}>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-muted-foreground">{t(msg`Destination location`)}</label>
            <Select
              class="w-44"
              value={dstLoc()}
              onChange={(v) => setDstLoc(v)}
              data-testid="picking-dst-select"
              aria-label={t(msg`Destination location`)}
              placeholder={t(msg`Select location`)}
              options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
            />
          </div>
        </Show>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Reference`)}</label>
          <input
            class="w-40 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder={t(msg`Optional`)}
            value={reference()}
            onInput={(e) => setReference(e.currentTarget.value)}
            data-testid="picking-reference-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="picking-create-button">
          {t(msg`Create operation`)}
        </Button>
      </form>

      {/* Unresolvable deep-link: URL requests an operation that does not exist / no
          access / offline. We show feedback instead of an empty panel. */}
      <Show
        when={
          openPickingId() && pickingResolution() !== "ready" && pickingResolution() !== "loading"
        }
      >
        <div class="mt-6" data-testid="picking-not-found">
          <NotFoundState
            variant={pickingResolution() === "offline" ? "offline" : "not-found"}
            title={pickingResolution() === "offline" ? undefined : t(msg`Operation not found`)}
            description={
              pickingResolution() === "offline"
                ? undefined
                : t(msg`This operation may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to operations`),
              onClick: () => {
                setOpenPickingId(null);
              },
            }}
          />
        </div>
      </Show>

      {/* Open operation editor (add lines + confirm). */}
      <Show when={openPicking()}>
        {(picking) => (
          <div
            class="mt-6 rounded-md border border-border p-4"
            data-testid="picking-editor"
            data-picking-id={picking().id}
            data-picking-state={picking().state ?? ""}
          >
            <div class="flex items-center justify-between">
              <h2 class="text-lg font-semibold">
                {kindLabel(picking().kind)}
                <Show when={picking().reference}>
                  {" "}
                  <span class="text-muted-foreground">#{picking().reference}</span>
                </Show>
              </h2>
              <span
                class="rounded-full border border-border px-2 py-0.5 text-xs"
                data-testid="picking-editor-state"
              >
                {picking().state === "done" ? t(msg`Done`) : t(msg`Draft`)}
              </span>
            </div>

            <Show when={picking().state !== "done"}>
              <div class="mt-4 flex flex-wrap items-end gap-3">
                <BarcodeScanInput
                  products={products.data ?? []}
                  onScan={(p) => setLineProduct(p.id)}
                />
                <div class="flex flex-col gap-1">
                  <label class="text-xs text-muted-foreground">{t(msg`Product`)}</label>
                  <Select
                    class="w-48"
                    value={lineProduct()}
                    onChange={(v) => {
                      setLineProduct(v);
                      setLineLot("");
                    }}
                    data-testid="line-product-select"
                    aria-label={t(msg`Product`)}
                    placeholder={t(msg`Select product`)}
                    options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                  />
                </div>
                <Show when={lineProductTracked()}>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs text-muted-foreground">
                      {t(msg`Lot / serial`)}
                      <Show when={picking().kind === "delivery" && fefoLot()}>
                        {" "}
                        <span class="text-primary" data-testid="line-fefo-hint">
                          {t(msg`(FEFO)`)}
                        </span>
                      </Show>
                    </label>
                    <Select
                      class="w-40"
                      value={lineLot()}
                      onChange={(v) => setLineLot(v)}
                      data-testid="line-lot-select"
                      aria-label={t(msg`Lot / serial`)}
                      placeholder={t(msg`Select lot`)}
                      options={(lineLots.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
                    />
                  </div>
                </Show>
                <div class="flex flex-col gap-1">
                  <label class="text-xs text-muted-foreground">{t(msg`Quantity`)}</label>
                  <input
                    class="w-28 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
                    placeholder="0"
                    inputmode="decimal"
                    value={lineQty()}
                    onInput={(e) => setLineQty(e.currentTarget.value)}
                    data-testid="line-qty-input"
                  />
                </div>
                <Button
                  onClick={() => void handleAddLine(lineProduct(), lineQty())}
                  disabled={!drizzle()}
                  data-testid="line-add-button"
                >
                  {t(msg`Add line`)}
                </Button>
              </div>
            </Show>

            <div class="mt-4" data-testid="line-list">
              <Show
                when={(openLines.data ?? []).length > 0}
                fallback={<p class="text-sm text-muted-foreground">{t(msg`No lines yet.`)}</p>}
              >
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-border text-left text-muted-foreground">
                      <th class="py-2 font-medium">{t(msg`Product`)}</th>
                      <th class="py-2 font-medium text-right">{t(msg`Quantity`)}</th>
                      <th class="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    <For each={openLines.data ?? []}>
                      {(line) => (
                        <tr
                          class="border-b border-border/50"
                          data-testid="line-row"
                          data-lot-id={line.lot_id ?? ""}
                        >
                          <td class="py-2">
                            {productName()(line.product_id ?? "")}
                            <Show when={line.lot_id}>
                              {" "}
                              <span
                                class="text-xs text-muted-foreground"
                                data-testid="line-lot-name"
                              >
                                ({lotName()(line.lot_id ?? "")})
                              </span>
                            </Show>
                          </td>
                          <td class="py-2 text-right tabular-nums" data-testid="line-qty">
                            {fromMinorQty(line.qty ?? 0)}
                          </td>
                          <td class="py-2 text-right">
                            <Show when={picking().state !== "done"}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => void handleDeleteLine(line.id)}
                                data-testid="line-delete-button"
                              >
                                {t(msg`Remove`)}
                              </Button>
                            </Show>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </Show>
            </div>

            <Show when={picking().state !== "done"}>
              <div class="mt-4 flex gap-2">
                <Button
                  onClick={() => void handleConfirm()}
                  disabled={!drizzle()}
                  data-testid="picking-confirm-button"
                >
                  {t(msg`Confirm operation`)}
                </Button>
                <Button variant="ghost" onClick={() => setOpenPickingId(null)}>
                  {t(msg`Close`)}
                </Button>
              </div>
            </Show>
          </div>
        )}
      </Show>

      {/* Lista de operaciones. */}
      <h2 class="mt-8 text-lg font-semibold">{t(msg`Recent operations`)}</h2>
      <div class="mt-3" data-testid="picking-list">
        <Show
          when={(pickings.data ?? []).length > 0}
          fallback={
            <EmptyState
              icon={ClipboardList}
              illustration={EmptyOrdersIllustration}
              title={t(msg`No operations yet`)}
              description={t(msg`Create a receipt, delivery or transfer to move stock.`)}
            />
          }
        >
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={pickings.data ?? []}>
              {(p) => (
                <li
                  class="flex items-center gap-3 px-4 py-3"
                  data-testid="picking-row"
                  data-picking-id={p.id}
                  data-picking-kind={p.kind ?? ""}
                  data-picking-state={p.state ?? ""}
                >
                  <ClipboardList class="h-5 w-5 text-primary shrink-0" />
                  <div class="flex-1 min-w-0">
                    <p class="font-medium">
                      {kindLabel(p.kind)}
                      <Show when={p.reference}>
                        {" "}
                        <span class="text-muted-foreground">#{p.reference}</span>
                      </Show>
                    </p>
                  </div>
                  <span
                    class="rounded-full border border-border px-2 py-0.5 text-xs"
                    data-testid="picking-row-state"
                  >
                    {p.state === "done" ? t(msg`Done`) : t(msg`Draft`)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOpenPickingId(p.id)}
                    data-testid="picking-open-button"
                  >
                    {t(msg`Open`)}
                  </Button>
                  <Show when={p.state !== "done"}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDeletePicking(p.id)}
                      data-testid="picking-delete-button"
                    >
                      {t(msg`Delete`)}
                    </Button>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </PageContainer>
  );
}

export const operationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/operations",
  validateSearch: (search: Record<string, unknown>): { picking?: string } => ({
    picking:
      typeof search.picking === "string" && search.picking.trim() !== ""
        ? search.picking
        : undefined,
  }),
  component: OperationsPage,
});
